import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { searchContent } from '../helpers/search-content';
import { useLanguage } from '../helpers/language-context';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const { language } = useLanguage();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure the DOM is ready for the transition
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for transition (300ms) before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setQuery('');
        setResults([]);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isAnimating]);

  const handleSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const searchResults = await searchContent(value, language);
      setResults(searchResults);
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!shouldRender) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[60] flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm transition-all duration-300 ${isAnimating ? 'opacity-100 backdrop-blur-sm' : 'opacity-0 backdrop-blur-none cursor-default'}`}>
      {/* Click outside to close */}
      <div className="absolute inset-0" onClick={handleClose}></div>

      <div className={`relative w-full max-w-2xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] transition-all duration-300 ${isAnimating ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 -translate-y-4 opacity-0'}`}>
        {/* Header / Input */}
        <div className="p-4 border-b border-gray-100 flex items-center gap-3">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" className="flex-shrink-0">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="flex-1 text-lg outline-none text-gray-800 placeholder-gray-400"
            placeholder="Search pages, news, stories..."
            value={query}
            onChange={handleSearch}
          />
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Results Area */}
        <div className="overflow-y-auto p-2 bg-gray-50 flex-1">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Searching...</div>
          ) : results.length > 0 ? (
            <div className="space-y-2">
              {results.map((item, index) => (
                <Link
                  key={index}
                  to={item.link}
                  className="block p-4 bg-white rounded-lg hover:shadow-md hover:border-blue-500 border border-transparent transition-all group"
                  onClick={handleClose}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 uppercase tracking-wide">
                      {item.type}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 group-hover:text-blue-600 transform group-hover:translate-x-1 transition-all">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">{item.title}</h3>
                  {item.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                  )}
                </Link>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-8 text-center text-gray-500">
              No results found for "{query}"
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400 text-sm">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;
